import { useState } from "react";
import { Droplet, Utensils, Syringe } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import MealForm from "../forms/MealForm";
import GlucoseForm from "../forms/GlucoseForm";
import DoseForm from "../forms/DoseForm";

/* Central register sheet: ONLY 3 options. The photo button is NOT shown here —
   it lives inside "Adicionar refeição". */
export default function RegisterSheet({ open, onOpenChange, onDone, initialStep = null }) {
  const { t } = useI18n();
  const { can } = useProfile();
  const [step, setStep] = useState(initialStep); // null | glucose | meal | dose

  function close() {
    onOpenChange(false);
    setTimeout(() => setStep(null), 200);
  }
  function done() {
    onDone?.();
    close();
  }

  const options = [
    { id: "glucose", title: t("reg_glucose"), sub: t("reg_glucose_sub"), Icon: Droplet, perm: "log_glucose" },
    { id: "meal", title: t("reg_meal"), sub: t("reg_meal_sub"), Icon: Utensils, perm: "log_meals" },
    { id: "dose", title: t("reg_dose"), sub: t("reg_dose_sub"), Icon: Syringe, perm: "log_doses" },
  ];

  const titles = {
    glucose: t("new_reading"),
    meal: t("add_meal"),
    dose: t("register_dose"),
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="register-sheet">
        <DialogHeader>
          <DialogTitle className="font-head text-xl text-[#0D253F]">
            {step ? titles[step] : t("register_q")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {step ? titles[step] : t("register_q")}
          </DialogDescription>
        </DialogHeader>

        {!step && (
          <div className="space-y-3 pt-1">
            {!options.some((o) => can(o.perm)) && (
              <p className="text-sm text-[#627D98] bg-[#F4F8FC] rounded-xl px-3 py-2" data-testid="view-only-notice">O seu acesso é só de visualização. Não pode fazer registos.</p>
            )}
            {options.map((o) => {
              const allowed = can(o.perm);
              return (
                <button
                  key={o.id}
                  disabled={!allowed}
                  onClick={() => setStep(o.id)}
                  data-testid={`register-option-${o.id}`}
                  className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-all ${
                    allowed
                      ? "border-[#E3EFF9] hover:border-[#1A73E8] hover:bg-[#F4F8FC] active:scale-[0.99]"
                      : "border-[#E3EFF9] opacity-50 cursor-not-allowed"
                  }`}
                >
                  <div className="h-11 w-11 rounded-xl bg-[#E3EFF9] flex items-center justify-center shrink-0">
                    <o.Icon className="text-[#1A73E8]" size={22} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0D253F]">{o.title}</div>
                    <div className="text-sm text-[#627D98]">{o.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {step === "glucose" && <GlucoseForm onSaved={done} />}
        {step === "meal" && <MealForm onSaved={done} />}
        {step === "dose" && <DoseForm onSaved={done} />}
      </DialogContent>
    </Dialog>
  );
}
